import type { GameStore } from '../core/store';
import type { Rng } from '../engine/rng';
import { BattleEngine, type BattleEvent, type BattleOutcome } from '../game/battle/battle';
import { getMove } from '../game/data/moves';
import { RISONANZA_LABEL } from '../game/data/risonanze';
import { getSpecies } from '../game/data/species';
import { isFainted, maxHp, type LumineInstance } from '../game/lumine';
import { battleGroundUrl, battlerUrl, propUrl } from './creatureArt';
import { el, sleep } from './dom';
import { pickTeamMember } from './teamMenu';

/**
 * UI di battaglia (DOM overlay, touch-first). Consuma gli eventi prodotti dal
 * BattleEngine headless e li mette in scena con barre HP animate, log e
 * piccoli effetti (shake/flash).
 */

interface CombatantView {
  root: HTMLDivElement;
  img: HTMLImageElement;
  name: HTMLSpanElement;
  level: HTMLSpanElement;
  fill: HTMLDivElement;
  hpText: HTMLSpanElement;
}

function combatantView(side: 'enemy' | 'ally'): CombatantView {
  const root = el('div', `combatant ${side}`);
  const img = el('img');
  const card = el('div', 'card');
  const nameRow = el('div', 'nome');
  const name = el('span');
  const level = el('span');
  nameRow.append(name, level);
  const bar = el('div', 'hp-bar');
  const fill = el('div', 'fill');
  bar.appendChild(fill);
  const hpText = el('span');
  hpText.style.fontSize = '12px';
  card.append(nameRow, bar, hpText);
  if (side === 'enemy') root.append(card, img);
  else root.append(img, card);
  return { root, img, name, level, fill, hpText };
}

export class BattleUi {
  private root: HTMLDivElement;
  private logEl: HTMLDivElement;
  private actionsEl: HTMLDivElement;
  private enemyView = combatantView('enemy');
  private allyView = combatantView('ally');
  private engine!: BattleEngine;
  private resolveBattle!: (outcome: BattleOutcome) => void;
  private busy = false;

  constructor(
    private uiRoot: HTMLElement,
    private store: GameStore,
    private rng: Rng,
  ) {
    this.root = el('div', 'battle');
    this.root.style.display = 'none';
    const field = el('div', 'battle-field');
    field.style.backgroundImage = `url('${battleGroundUrl()}')`;
    const deco = el('div', 'battle-deco');
    for (const [prop, cls] of [
      ['tree-large', 'deco-tree-l'],
      ['tree-medium', 'deco-tree-r'],
      ['bush-large', 'deco-bush-l'],
      ['bush-medium', 'deco-bush-r'],
    ] as const) {
      const img = el('img', cls);
      img.src = propUrl(prop);
      img.alt = '';
      deco.appendChild(img);
    }
    field.append(deco, this.enemyView.root, this.allyView.root);
    const panel = el('div', 'battle-panel');
    this.logEl = el('div', 'battle-log');
    this.actionsEl = el('div', 'battle-actions');
    panel.append(this.logEl, this.actionsEl);
    this.root.append(field, panel);
    uiRoot.appendChild(this.root);
  }

  /** Avvia una battaglia e risolve quando finisce. */
  start(team: LumineInstance[], wild: BattleEngine['enemy']): Promise<BattleOutcome> {
    this.engine = new BattleEngine(team, wild, this.rng);
    this.root.style.display = 'flex';
    this.refreshCombatant(this.enemyView, this.engine.enemy);
    this.refreshCombatant(this.allyView, this.engine.active);
    const wildSpecies = getSpecies(wild.speciesId);
    this.log(`Un ${wildSpecies.name} selvatico è apparso!`);
    this.showMainActions();
    return new Promise((resolve) => {
      this.resolveBattle = resolve;
    });
  }

  private log(text: string): void {
    this.logEl.textContent = text;
  }

  private refreshCombatant(view: CombatantView, lumine: LumineInstance): void {
    const species = getSpecies(lumine.speciesId);
    view.img.src = battlerUrl(lumine.speciesId);
    view.img.alt = species.name;
    view.name.textContent = `${species.name} (${RISONANZA_LABEL[species.risonanza]})`;
    this.refreshHp(view, lumine);
  }

  private refreshHp(view: CombatantView, lumine: LumineInstance): void {
    const max = maxHp(lumine);
    const ratio = Math.max(0, lumine.hp / max);
    view.level.textContent = `Lv.${lumine.level}`;
    view.fill.style.width = `${ratio * 100}%`;
    view.fill.className = 'fill' + (ratio <= 0.25 ? ' low' : ratio <= 0.5 ? ' warn' : '');
    view.hpText.textContent = `${Math.max(0, lumine.hp)}/${max} PS`;
  }

  private showMainActions(): void {
    this.actionsEl.replaceChildren();
    const lotta = el('button', 'btn primary', '⚔ Lotta');
    lotta.addEventListener('click', () => this.showMoves());
    const cattura = el(
      'button',
      'btn',
      `◉ Risonatore (${this.store.state.player.inventory.risonatori})`,
    );
    cattura.addEventListener('click', () => this.onCapture());
    const squadra = el('button', 'btn', '↺ Squadra');
    squadra.addEventListener('click', () => this.onSwitch());
    const fuga = el('button', 'btn', '🏃 Fuga');
    fuga.addEventListener('click', () => this.act({ kind: 'flee' }));
    this.actionsEl.append(lotta, cattura, squadra, fuga);
  }

  private showMoves(): void {
    this.actionsEl.replaceChildren();
    this.engine.active.moves.forEach((moveId, index) => {
      const move = getMove(moveId);
      const btn = el(
        'button',
        'btn',
        `${move.name} · ${RISONANZA_LABEL[move.risonanza]} · ${move.power}`,
      );
      btn.title = move.description;
      btn.addEventListener('click', () => this.act({ kind: 'move', moveIndex: index }));
      this.actionsEl.appendChild(btn);
    });
    const back = el('button', 'btn', '← Indietro');
    back.addEventListener('click', () => this.showMainActions());
    this.actionsEl.appendChild(back);
  }

  private async onCapture(): Promise<void> {
    if (this.busy) return;
    if (!this.store.useRisonatore()) {
      this.log('Non hai più Risonatori!');
      return;
    }
    await this.act({ kind: 'capture' });
  }

  private async onSwitch(): Promise<void> {
    if (this.busy) return;
    const index = await pickTeamMember(this.uiRoot, this.engine.team, {
      title: 'Chi vuoi schierare?',
      cancellable: true,
      excludeIndex: this.engine.activeIndex,
    });
    if (index === null) return;
    await this.act({ kind: 'switch', teamIndex: index });
  }

  private async act(action: Parameters<BattleEngine['takeTurn']>[0]): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.actionsEl.replaceChildren();
    const events = this.engine.takeTurn(action);
    await this.playEvents(events);
    this.busy = false;
    if (!this.engine.outcome) {
      if (isFainted(this.engine.active)) {
        // KO: cambio obbligatorio (gratuito)
        const index = await pickTeamMember(this.uiRoot, this.engine.team, {
          title: 'Il tuo Lumine è esausto! Chi schieri?',
          cancellable: false,
        });
        if (index !== null) {
          await this.playEvents(this.engine.replaceFainted(index));
        }
      }
      this.showMainActions();
    }
  }

  private async playEvents(events: BattleEvent[]): Promise<void> {
    for (const event of events) {
      switch (event.kind) {
        case 'message':
          this.log(event.text);
          await sleep(850);
          break;
        case 'damage': {
          const view = event.target === 'enemy' ? this.enemyView : this.allyView;
          const lumine = event.target === 'enemy' ? this.engine.enemy : this.engine.active;
          view.img.classList.remove('shake');
          void view.img.offsetWidth; // riavvia l'animazione CSS
          view.img.classList.add('shake');
          this.refreshHp(view, lumine);
          if (event.effectiveness > 1) {
            this.log('È superefficace!');
            await sleep(700);
          } else if (event.effectiveness < 1) {
            this.log('Non è molto efficace…');
            await sleep(700);
          } else {
            await sleep(450);
          }
          break;
        }
        case 'faint': {
          const lumine = event.target === 'enemy' ? this.engine.enemy : this.engine.active;
          this.log(`${getSpecies(lumine.speciesId).name} è esausto!`);
          await sleep(850);
          break;
        }
        case 'captureAttempt': {
          this.enemyView.img.classList.remove('flash');
          void this.enemyView.img.offsetWidth;
          this.enemyView.img.classList.add('flash');
          this.log(event.success ? 'Il Risonatore si sincronizza… catturato!' : 'Si è liberato!');
          await sleep(1000);
          break;
        }
        case 'xp':
          this.log(
            event.leveledUp
              ? `+${event.amount} EXP — sale al livello ${event.newLevel}!`
              : `+${event.amount} EXP`,
          );
          this.refreshHp(this.allyView, this.engine.active);
          await sleep(900);
          break;
        case 'switch':
          this.refreshCombatant(this.allyView, this.engine.active);
          this.log(`Vai, ${getSpecies(this.engine.active.speciesId).name}!`);
          await sleep(700);
          break;
        case 'fled':
          this.log(event.success ? 'Sei fuggito senza problemi.' : 'Non riesci a fuggire!');
          await sleep(800);
          break;
        case 'end':
          await sleep(400);
          this.close(event.outcome);
          break;
      }
    }
  }

  private close(outcome: BattleOutcome): void {
    this.root.style.display = 'none';
    this.resolveBattle(outcome);
  }
}
